import React, { forwardRef } from 'react';
import { Link } from 'gatsby';

const isImage = (value) =>
  Boolean(value && typeof value === 'object' && typeof value.url === 'string');
const isText = (value) => typeof value === 'string' && value.trim().length > 0;

const SUPERSCRIPT_SPLIT = /([®™©])/;
const SUPERSCRIPT_CHAR = /[®™©]/;
const BOLD_SPLIT = /(\*\*[^*]+\*\*)/;
const BOLD_MATCH = /^\*\*([^*]+)\*\*$/;

const withSuperscripts = (text, keyBase) => {
  let offset = 0;

  return text
    .split(SUPERSCRIPT_SPLIT)
    .filter(Boolean)
    .map((part) => {
      const key = `${keyBase}-${offset}`;
      offset += part.length;

      return SUPERSCRIPT_CHAR.test(part) ? (
        <sup key={key} className="hkx-hero__reg">
          {part}
        </sup>
      ) : (
        part
      );
    });
};

const renderHeadline = (text) => {
  let offset = 0;

  return text
    .split(BOLD_SPLIT)
    .filter(Boolean)
    .flatMap((part) => {
      const key = `seg-${offset}`;
      offset += part.length;
      const bold = part.match(BOLD_MATCH);

      return bold
        ? [<strong key={key}>{withSuperscripts(bold[1], `${key}-b`)}</strong>]
        : withSuperscripts(part, key);
    });
};

const HeroSlide = forwardRef((props, ref) => {
  const {
    background,
    backgroundMobile,
    product,
    productMobile,
    badgeIcon,
    headline,
    subhead,
    badgeText,
    ctaText,
    ctaLink,
    legal,
    productAlt,
    claims,
  } = props;

  if (!isImage(product) && !isText(headline)) return null;

  const hasText = isText(headline) || isText(subhead);
  const hasContent = hasText || isText(ctaText);
  const isFeatured = isImage(product) && !hasText;

  const backgroundStyle = isImage(background)
    ? { backgroundImage: `url(${background.url})` }
    : undefined;

  const link = isText(ctaLink) ? ctaLink.trim() : '';
  const isInternalLink = link.startsWith('/');

  const renderCta = (extraClass) => {
    if (!isText(ctaText)) return null;
    const className = extraClass ? `hkx-hero__cta ${extraClass}` : 'hkx-hero__cta';
    if (isInternalLink) {
      return (
        <Link className={className} to={link}>
          {ctaText}
        </Link>
      );
    }

    return (
      <a className={className} href={link || undefined} rel="noopener noreferrer">
        {ctaText}
      </a>
    );
  };

  if (isFeatured) {
    return (
      <section ref={ref} className="hkx-hero hkx-hero--featured">
        {isImage(background) ? (
          <img
            className="hkx-hero__bg hkx-hero__bg--desktop"
            src={background.url}
            alt=""
            loading="eager"
          />
        ) : null}
        {isImage(backgroundMobile) ? (
          <img
            className="hkx-hero__bg hkx-hero__bg--mobile"
            src={backgroundMobile.url}
            alt=""
            loading="eager"
          />
        ) : null}

        <img
          className="hkx-hero__product hkx-hero__product--desktop"
          src={product.url}
          alt={productAlt || 'Tempra'}
          loading="eager"
        />
        {isImage(productMobile) ? (
          <img
            className="hkx-hero__product hkx-hero__product--mobile"
            src={productMobile.url}
            alt=""
            loading="eager"
          />
        ) : null}

        {isImage(claims) ? (
          <img
            className="hkx-hero__claims hkx-hero__claims--desktop"
            src={claims.url}
            alt=""
            loading="eager"
          />
        ) : null}

        {renderCta('hkx-hero__cta--featured')}

        {isText(legal) ? <p className="hkx-hero__legal">{legal}</p> : null}
      </section>
    );
  }

  return (
    <section ref={ref} className="hkx-hero" style={backgroundStyle}>
      <div className="hkx-hero__inner">
        {hasContent ? (
          <div className="hkx-hero__content">
            <div className="hkx-hero__text">
              {isText(headline) ? (
                <h2 className="hkx-hero__headline">{renderHeadline(headline)}</h2>
              ) : null}
              {isText(subhead) ? <p className="hkx-hero__subhead">{subhead}</p> : null}
            </div>
            {renderCta()}
          </div>
        ) : null}

        <div className="hkx-hero__media">
          {isImage(product) ? (
            <img
              className="hkx-hero__product"
              src={product.url}
              alt={productAlt || headline || 'Tempra'}
              width="400"
              height="366"
              loading="eager"
            />
          ) : null}

          {isImage(badgeIcon) || isText(badgeText) ? (
            <div className="hkx-hero__badge">
              {isImage(badgeIcon) ? (
                <span className="hkx-hero__badge-icon">
                  <img src={badgeIcon.url} alt="" width="40" height="40" />
                </span>
              ) : null}
              {isText(badgeText) ? <span className="hkx-hero__badge-text">{badgeText}</span> : null}
            </div>
          ) : null}
        </div>
      </div>

      {isText(legal) ? <p className="hkx-hero__legal">{legal}</p> : null}
    </section>
  );
});

HeroSlide.displayName = 'HeroSlide';

export default HeroSlide;
